import { Controller } from "@hotwired/stimulus"

/**
 * Shows/hides the task-type-specific filter sections in the beta tasks
 * Filters dropdown based on the selected Task Types. Hidden sections get
 * their inputs disabled so stale values are dropped on submit.
 *
 * Also narrows the Status multi-select to the selected task types' status
 * groups (via the group -> task type map on the statusFilter target).
 * 'Draft' has no mapped type and always shows; with no Task Type selected
 * every group shows.
 */
export default class extends Controller {
  static targets = ["typeSelect", "section", "statusFilter"]

  connect() {
    this.update()
  }

  update() {
    const selectedTypes = this.selectedTaskTypes()

    this.sectionTargets.forEach(section => {
      const sectionTypes = (section.dataset.taskTypes || '').split(' ')
      const visible = sectionTypes.some(type => selectedTypes.includes(type))

      section.classList.toggle('d-none', !visible)
      section.querySelectorAll('input, select').forEach(el => {
        el.disabled = !visible
      })
    })

    this.syncStatusGroups(selectedTypes)

    // Bubbles to window so dropdowns outside this form (Columns) can
    // narrow to the pending selection without waiting for Apply
    this.dispatch('types-changed', { detail: { types: selectedTypes } })
  }

  syncStatusGroups(selectedTypes) {
    if (!this.hasStatusFilterTarget) return

    const groupTypes = JSON.parse(this.statusFilterTarget.dataset.statusGroupTypes || '{}')

    this.statusFilterTarget.querySelectorAll('[data-multi-select-dropdown-target="group"]').forEach(group => {
      const type = groupTypes[group.dataset.group]
      const visible = !type || selectedTypes.length === 0 || selectedTypes.includes(type)

      group.classList.toggle('d-none', !visible)
      // forceHidden keeps the multi-select's own search filter from
      // re-showing the group when an option label matches the query
      if (visible) {
        delete group.dataset.forceHidden
      } else {
        group.dataset.forceHidden = 'true'
      }
      group.querySelectorAll('input').forEach(el => {
        el.disabled = !visible
      })
    })

    this.refreshStatusDisplay()
  }

  // Checked boxes in a group that just got hidden are disabled (won't
  // submit), so the multi-select's button chips need recomputing.
  refreshStatusDisplay() {
    const multiSelect = this.statusFilterTarget.querySelector('[data-controller~="multi-select-dropdown"]')
    if (!multiSelect) return

    const controller = this.application.getControllerForElementAndIdentifier(multiSelect, 'multi-select-dropdown')
    if (!controller) return

    controller.updateDisplay()
    controller.updateSelectedBadges()
  }

  selectedTaskTypes() {
    if (!this.hasTypeSelectTarget) return []

    return Array.from(this.typeSelectTarget.selectedOptions).map(option => option.value)
  }
}
